import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Mail, User, MessageSquare, CheckCircle, HelpCircle, Shield, Globe, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import api from '../services/api';

export default function HelpPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/contact', formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: "How does matching work?",
      answer: "When you and another user both swipe right (like) on each other, it's a match! You can then start chatting."
    },
    {
      question: "How do I verify my profile?",
      answer: "Go to your Profile settings and look for the verification option. Follow the steps to verify your identity."
    },
    {
      question: "What are Super Likes?",
      answer: "Super Likes let someone know you're especially interested. Free users get 1 per day, Premium users get more."
    },
    {
      question: "How do I report a user?",
      answer: "Visit the Safety Center from the menu, or tap the report button on any profile to report suspicious behavior."
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center p-6" data-testid="help-success">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-sm"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="font-heading text-2xl text-white mb-2">Message Sent!</h1>
          <p className="text-gray-400 font-body mb-8">
            Thank you for contacting us. We'll get back to you within 24-48 hours.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="btn-gold px-8 py-3 rounded-full font-body font-semibold"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-12" data-testid="help-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-heading text-2xl text-white">Help & Contact</h1>
            <p className="text-sm text-gray-400 font-body">We're here to help</p>
          </div>
        </div>
      </header>

      <div className="px-6 py-6">
        {/* Quick Links */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <button
            onClick={() => navigate('/safety')}
            className="p-4 bg-dark-paper border border-white/5 rounded-xl text-center hover:border-gold/30 transition-colors"
          >
            <Shield className="w-6 h-6 text-gold mx-auto mb-2" />
            <p className="text-white text-xs font-body">Safety</p>
          </button>
          <button
            onClick={() => navigate('/country-guides')}
            className="p-4 bg-dark-paper border border-white/5 rounded-xl text-center hover:border-gold/30 transition-colors"
          >
            <Globe className="w-6 h-6 text-gold mx-auto mb-2" />
            <p className="text-white text-xs font-body">Guides</p>
          </button>
          <button
            onClick={() => navigate('/success-stories')}
            className="p-4 bg-dark-paper border border-white/5 rounded-xl text-center hover:border-gold/30 transition-colors"
          >
            <Heart className="w-6 h-6 text-gold mx-auto mb-2" />
            <p className="text-white text-xs font-body">Stories</p>
          </button>
        </div>

        {/* FAQs */}
        <div className="mb-8">
          <h2 className="font-heading text-xl text-white mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-gold" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-dark-paper border border-white/5 rounded-xl p-4"
              >
                <h3 className="font-body font-semibold text-white mb-2">{faq.question}</h3>
                <p className="text-gray-400 text-sm font-body">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-dark-paper border border-white/5 rounded-2xl p-6">
          <h2 className="font-heading text-xl text-white mb-2">Contact Us</h2>
          <p className="text-gray-400 text-sm font-body mb-6">
            Can't find what you're looking for? Send us a message and we'll help you out.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm font-body">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2 font-body">
                <User className="w-4 h-4 inline mr-2" />
                Your Name
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                className="bg-dark-elevated border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                data-testid="contact-name"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2 font-body">
                <Mail className="w-4 h-4 inline mr-2" />
                Email Address
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className="bg-dark-elevated border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                data-testid="contact-email"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2 font-body">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="How can we help you?"
                rows={4}
                className="w-full bg-dark-elevated border border-white/10 text-white rounded-xl p-4 placeholder:text-gray-500 focus:outline-none focus:border-gold/50 font-body resize-none"
                data-testid="contact-message"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-gold rounded-full font-body font-semibold flex items-center justify-center gap-2"
              data-testid="contact-submit"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Support Info */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm font-body">
            Typical response time: 24-48 hours
          </p>
          <p className="text-gray-500 text-xs font-body mt-1">
            support@africanconnections.com
          </p>
        </div>
      </div>
    </div>
  );
}
